// frontend/src/components/common/Breadcrumbs.js
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
  Box
} from '@mui/material';
import {
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';

// Human-readable labels for route segments
const ROUTE_LABELS = {
  dashboard: 'Dashboard',
  projects: 'Projects',
  create: 'Create',
  edit: 'Edit',
  review: 'Review',
  'translation-memories': 'Translation Memories',
  glossaries: 'Glossaries',
  users: 'Users',
  analytics: 'Analytics',
  productivity: 'Productivity',
  languages: 'Languages',
  costs: 'Cost Analysis',
  quality: 'Quality',
  consistency: 'Consistency',
  batching: 'Batch Analytics',
  workflow: 'Workflow',
  settings: 'Settings',
  'mt-configurations': 'MT Configurations',
  'mt-usage': 'MT Usage',
  'llm-configurations': 'LLM Configurations',
  'llm-prompt-templates': 'Prompt Templates',
  'auto-approval-rules': 'Auto-Approval Rules',
  'format-templates': 'Format Templates',
  'qa-profiles': 'QA Profiles',
  billing: 'Billing',
  organizations: 'Organizations',
  'text-translation': 'Text Translation',
  profile: 'Profile',
  platform: 'Platform Admin',
  'mt-engines': 'MT Engines',
  'llm-providers': 'LLM Providers',
  revenue: 'Revenue',
  usage: 'Usage',
  webhooks: 'Webhooks',
  support: 'Support',
  tickets: 'Tickets',
  checkout: 'Checkout',
  upgrade: 'Upgrade Plan',
};

// Routes where breadcrumbs should be hidden (top-level single pages)
const HIDDEN_ROUTES = ['/dashboard', '/login', '/signup'];

// Check if a segment looks like a UUID
const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

export default function Breadcrumbs() {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Don't show breadcrumbs on hidden routes or when path is too short
  if (pathSegments.length <= 1 || HIDDEN_ROUTES.includes(location.pathname)) {
    return null;
  }

  const crumbs = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const isLast = index === pathSegments.length - 1;

    // For UUIDs, show "Details" instead of the ID
    let label = isUUID(segment) ? 'Details' : (ROUTE_LABELS[segment] || segment);

    return { label, path, isLast };
  });

  return (
    <Box sx={{ mb: 2 }}>
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}
      >
        <Link
          component={RouterLink}
          to="/dashboard"
          underline="hover"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
          Home
        </Link>
        {crumbs.map((crumb) =>
          crumb.isLast ? (
            <Typography
              key={crumb.path}
              color="text.primary"
              sx={{ fontSize: '0.875rem', fontWeight: 500 }}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Link
              key={crumb.path}
              component={RouterLink}
              to={crumb.path}
              underline="hover"
              color="text.secondary"
              sx={{ fontSize: '0.875rem' }}
            >
              {crumb.label}
            </Link>
          )
        )}
      </MuiBreadcrumbs>
    </Box>
  );
}
